import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { AttachmentBase } from 'src/sb-tools/typescript/models/attachment-base.model';
import WZoom from 'vanilla-js-wheel-zoom';
import { SizeMetadata } from '@sb-tools/typescript/models/size-metadata.model';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderComponent } from '@sb-tools/typescript/components/loader/loader.component';
import { InlineSVGComponent } from '../../../Frontend-Inline-SVG-Module/inline-svg.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

type WZoom = typeof WZoom;

const INITIAL_IMAGE_SIZE = 1;
const MAX_AND_MIN_SCALES_DIFFERENCE = 2;

@Component({
    selector: 'sb-file-view-image',
    templateUrl: './file-view-image.component.html',
    styleUrls: ['./file-view-image.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, LoaderComponent, InlineSVGComponent, TooltipModule],
})
export class FileViewImageComponent implements OnChanges, OnDestroy {
    @Input() attachment: AttachmentBase;

    isLoading = true;
    isMaxZoomReached = false;
    isMinZoomReached = true;
    wzoom: WZoom;

    private imageContainer: HTMLElement | null;
    private imageHeight: number;
    private imageWidth: number;
    private maxScale = INITIAL_IMAGE_SIZE;
    private minScale: number;
    private zoomedImageArea: HTMLElement | null;

    constructor(private changeDetectorRef: ChangeDetectorRef) {
        this.handleWindowResize = this.handleWindowResize.bind(this);
    }

    ngOnChanges(): void {
        this.isLoading = true;
        this.changeDetectorRef.detectChanges();

        setTimeout(() => {
            const isNoImageSizeData =
                !this.attachment.sizeMetadata?.dimensions?.height || !this.attachment.sizeMetadata?.dimensions?.width;

            if (isNoImageSizeData) {
                this.loadImageSizeAndContinue(this.attachment.url);
            } else {
                this.initImageZoomSettings();
            }
        });
    }

    preventModalClose(event: Event): void {
        event.stopPropagation();
    }

    resetZoom(): void {
        this.wzoom?.prepare();
    }

    zoomIn(): void {
        this.wzoom?.zoomUp();
    }

    zoomOut(): void {
        this.wzoom?.zoomDown();
    }

    private calculateScaleValues(): void {
        this.minScale = Math.min(
            this.imageContainer.offsetWidth / this.imageWidth,
            this.imageContainer.offsetHeight / this.imageHeight,
        );

        if (this.minScale >= INITIAL_IMAGE_SIZE) {
            this.minScale = INITIAL_IMAGE_SIZE;
        }

        this.maxScale = this.minScale + MAX_AND_MIN_SCALES_DIFFERENCE;
    }

    private initImageZoomSettings(): void {
        this.zoomedImageArea = document.getElementById('zoomedImageArea');
        this.imageContainer = document.getElementById('imageContainer');

        this.imageHeight = this.attachment.sizeMetadata.dimensions.height;
        this.imageWidth = this.attachment.sizeMetadata.dimensions.width;

        this.zoomedImageArea.style.cursor = 'zoom-in';

        this.calculateScaleValues();

        this.wzoom = WZoom.create('#zoomedImageArea', {
            type: 'html',
            width: this.imageWidth,
            height: this.imageHeight,
            minScale: this.minScale,
            maxScale: this.maxScale,
            speed: 10,
            dragScrollableOptions: {
                onGrab: () => {
                    this.zoomedImageArea.style.cursor = 'grabbing';
                    this.zoomedImageArea.style.transition = 'none';
                },
                onDrop: () => {
                    this.setCursorType();
                },
            },
            rescale: () => {
                this.setCursorType();
            },
            prepare: () => {
                this.setCursorType();
            },
        });

        window.addEventListener('resize', this.handleWindowResize);

        setTimeout(() => {
            this.setCursorType();
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
        }, 100);
    }

    private getCriticalZoomValuesReached(): void {
        this.isMaxZoomReached = this.wzoom.content?.currentScale === this.wzoom.content.maxScale;
        this.isMinZoomReached = this.wzoom.content?.currentScale === this.wzoom.content.minScale;
        this.changeDetectorRef.detectChanges();
    }

    private handleWindowResize(): void {
        this.wzoom?.prepare();
    }

    private loadImageSizeAndContinue(url: string): void {
        const img = new Image();
        img.onload = () => {
            this.attachment.sizeMetadata = {
                dimensions: {
                    height: img.height,
                    width: img.width,
                },
            } as SizeMetadata;

            this.initImageZoomSettings();
        };
        img.src = url;
    }

    private setCursorType(): void {
        if (this.wzoom) {
            this.getCriticalZoomValuesReached();

            if (this.isMaxZoomReached) {
                this.zoomedImageArea.style.cursor = 'zoom-out';
            } else {
                this.zoomedImageArea.style.cursor = 'zoom-in';
            }
        }
    }

    ngOnDestroy(): void {
        this.wzoom?.destroy();
        window.removeEventListener('resize', this.handleWindowResize);
    }
}
